/**
 * Shared API contract for SmartKargo IT Asset Hub V3.
 *
 * Enumerated business values (statuses, conditions, retirement methods, roles) are
 * deliberately typed as `string`: the authoritative lists live in the database's
 * CHECK constraints and are discovered by the API at startup (see /api/v1/meta).
 */
import { z } from 'zod';

// ── Roles & capabilities ─────────────────────────────────────

export type Role = 'SuperAdmin' | 'Admin' | 'Auditor' | 'Employee';

export type Capability =
  | 'inventory.read'
  | 'inventory.write'
  | 'people.read'
  | 'people.write'
  | 'settings.read'
  | 'insights.read'
  | 'recovery.manage'
  | 'audit.read'
  | 'import.run'
  | 'reports.export'
  | 'roles.manage'
  | 'licenses.read'
  | 'licenses.write'
  | 'self.devices';

const CAPABILITIES: Record<Role, readonly Capability[]> = {
  SuperAdmin: ['settings.read', 'recovery.manage', 'insights.read', 'inventory.read', 'inventory.write', 'people.read', 'people.write', 'audit.read', 'import.run', 'reports.export', 'roles.manage', 'licenses.read', 'licenses.write'],
  // Admins hold every Super Admin right, plus their own devices page (they have an employee record).
  Admin: ['settings.read', 'recovery.manage', 'insights.read', 'inventory.read', 'inventory.write', 'people.read', 'people.write', 'audit.read', 'import.run', 'reports.export', 'roles.manage', 'licenses.read', 'licenses.write', 'self.devices'],
  Auditor: ['inventory.read', 'people.read', 'audit.read', 'reports.export', 'licenses.read', 'self.devices'],
  Employee: ['self.devices'],
};

export function can(role: Role | null | undefined, capability: Capability): boolean {
  return !!role && CAPABILITIES[role].includes(capability);
}

export function capabilitiesFor(role: Role): Capability[] {
  return [...CAPABILITIES[role]];
}

/** Staff = anyone who can see organisation-wide inventory. */
export const isStaff = (role: Role | null | undefined) => can(role, 'inventory.read');

// ── Primitives ───────────────────────────────────────────────

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
/** True for a real calendar day: 2026-02-30 and 2026-13-01 match the format but are not dates. */
export const isRealDate = (v: string) => {
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the format YYYY-MM-DD')
  .refine(isRealDate, 'Not a real date')
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));
const ciaScore = z.coerce.number().int().min(1).max(3).nullable().optional();
const uuid = z.guid('Not a valid identifier');
const rowVersion = z.string().regex(/^[0-9a-fA-F]{16}$/, 'Missing or invalid row version');

// ── Assets ───────────────────────────────────────────────────

export const ASSET_SORT_FIELDS = ['assetTag', 'name', 'assetType', 'status', 'model', 'assignee', 'team', 'updatedAt', 'assignedDate'] as const;
export type AssetSortField = (typeof ASSET_SORT_FIELDS)[number];

export const assetQuerySchema = z.object({
  q: trimmed(120).optional(),
  type: trimmed(120).optional(),
  status: trimmed(20).optional(),
  team: trimmed(160).optional(),
  location: trimmed(200).optional(),
  assigned: z.enum(['yes', 'no']).optional(),
  laptop: z.enum(['1']).optional(),
  employeeId: uuid.optional(),
  quality: z.enum(['no-serial', 'no-warranty']).optional(),
  sort: z.enum(ASSET_SORT_FIELDS).default('assetTag'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});
export type AssetQuery = z.infer<typeof assetQuerySchema>;

/** Same rule as the warranty editor: a warranty cannot end before the purchase. */
const warrantyAfterPurchase = (v: { purchaseDate?: string | null; warrantyExpiry?: string | null }) =>
  !v.purchaseDate || !v.warrantyExpiry || v.warrantyExpiry >= v.purchaseDate;
const WARRANTY_ORDER = { message: 'Warranty cannot end before purchase', path: ['warrantyExpiry'] };

const assetFields = {
  name: trimmed(240).min(1, 'Name is required'),
  assetType: trimmed(120).min(1, 'Category is required'),
  model: optionalText(200),
  vendor: optionalText(160),
  serialNumber: optionalText(160),
  location: optionalText(200),
  operatingSystem: optionalText(160),
  cpu: optionalText(160),
  ram: optionalText(80),
  storage: optionalText(100),
  purchaseDate: isoDate,
  warrantyExpiry: isoDate,
  notes: optionalText(2000),
  confidentiality: ciaScore,
  integrity: ciaScore,
  availability: ciaScore,
};

export const assetCreateSchema = z.object({
  assetTag: trimmed(100)
    .min(1, 'Asset tag is required')
    .regex(/^[A-Za-z0-9][A-Za-z0-9._\-/ ]*$/, 'Asset tag may contain letters, digits, space, . _ - /'),
  status: trimmed(20).min(1, 'Status is required'),
  ...assetFields,
}).refine(warrantyAfterPurchase, WARRANTY_ORDER);
export type AssetCreateInput = z.infer<typeof assetCreateSchema>;

export const assetUpdateSchema = z.object({ rowVersion, ...assetFields }).refine(warrantyAfterPurchase, WARRANTY_ORDER);
export type AssetUpdateInput = z.infer<typeof assetUpdateSchema>;

export const assignSchema = z.object({
  rowVersion,
  employeeId: uuid,
  reason: optionalText(160),
  ticketReference: optionalText(160),
  handoverCondition: optionalText(20),
  note: optionalText(500),
  /** Email the employee (manager in CC) once the assignment is saved. */
  notify: z.boolean().default(true),
});
export type AssignInput = z.infer<typeof assignSchema>;

export const returnSchema = z.object({
  rowVersion,
  targetStatus: trimmed(20).min(1),
  returnCondition: optionalText(20),
  note: optionalText(500),
  /** Email the employee (manager in CC) once the return is saved. */
  notify: z.boolean().default(true),
});
export type ReturnInput = z.infer<typeof returnSchema>;

export const statusChangeSchema = z.object({
  rowVersion,
  status: trimmed(20).min(1),
  note: trimmed(500).min(3, 'Give a short reason (at least 3 characters)'),
});
export type StatusChangeInput = z.infer<typeof statusChangeSchema>;

export const retireSchema = z.object({
  rowVersion,
  method: trimmed(30).min(1, 'Retirement method is required'),
  reason: trimmed(500).min(3, 'Give a short reason (at least 3 characters)'),
  sanitization: optionalText(20),
});
export type RetireInput = z.infer<typeof retireSchema>;

export const reinstateSchema = z.object({
  rowVersion,
  reason: trimmed(500).min(3, 'Give a short reason (at least 3 characters)'),
});
export type ReinstateInput = z.infer<typeof reinstateSchema>;

// ── Employees ────────────────────────────────────────────────

export const employeeQuerySchema = z.object({
  q: trimmed(120).optional(),
  team: trimmed(160).optional(),
  devices: z.enum(['with', 'without', 'laptop', 'no-laptop', 'multi-laptop']).optional(),
  quality: z.enum(['no-email', 'multi-id']).optional(),
  role: trimmed(30).optional(),
  /** Leavers are hidden unless asked for. */
  status: z.enum(['active', 'inactive', 'all']).default('active'),
  sort: z.enum(['displayName', 'team', 'employeeId', 'devices']).default('displayName'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});
export type EmployeeQuery = z.infer<typeof employeeQuerySchema>;

export const roleChangeSchema = z.object({ role: trimmed(30).min(1) });

const workEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(320)
  .email('Enter a valid email address');

const employeeFields = {
  displayName: trimmed(200).min(2, 'Full name is required'),
  email: workEmail,
  team: optionalText(160),
  designation: optionalText(160),
  managerName: optionalText(200),
  managerEmail: workEmail.nullable().optional().or(z.literal('').transform(() => null)),
  dateOfJoining: isoDate,
};

const employeeCode = trimmed(80)
  .min(2, 'Employee ID is required')
  .regex(/^[A-Za-z0-9][A-Za-z0-9._\-/]*$/, 'Employee ID may contain letters, digits, . _ - /');

/** New joiner. Starts with the Employee portal role; only the Super Admin grants more. */
export const employeeCreateSchema = z.object({ employeeCode, ...employeeFields });
export type EmployeeCreateInput = z.infer<typeof employeeCreateSchema>;

/**
 * Profile edit. `updatedAt` (from the detail) guards against overwriting someone else's change.
 * `employeeCode` is optional: omitted keeps the current ID; a new value corrects or replaces it (people.write only).
 */
export const employeeUpdateSchema = z.object({ updatedAt: z.string().min(10), employeeCode: employeeCode.optional(), ...employeeFields });
export type EmployeeUpdateInput = z.infer<typeof employeeUpdateSchema>;

/**
 * Other employee IDs a person has been known by (hub.EmployeeAliases): an intern ID before onboarding, an overseas
 * entity ID after a transfer, a contractor ID, or an older ID. The current ID stays on the employee record.
 */
export const EMPLOYEE_ALIAS_KINDS = ['Intern', 'Overseas', 'Contractor', 'Previous', 'Other'] as const;
export type EmployeeAliasKind = (typeof EMPLOYEE_ALIAS_KINDS)[number];
export const employeeAliasSchema = z.object({
  code: trimmed(80)
    .min(2, 'Employee ID is required')
    .regex(/^[A-Za-z0-9][A-Za-z0-9._\-/]*$/, 'Employee ID may contain letters, digits, . _ - /')
    .transform((v) => v.toUpperCase()),
  kind: z.enum(EMPLOYEE_ALIAS_KINDS),
  note: optionalText(300),
});
export type EmployeeAliasInput = z.infer<typeof employeeAliasSchema>;

export const OFFBOARD_REASONS = ['Resigned', 'Terminated', 'End of contract', 'Retirement', 'Transfer', 'Other'] as const;

/**
 * Offboarding, in one transaction: every held device is returned to IT (to `returnStatus`), every open
 * licence seat is released (if `releaseSeats`), and the person is deactivated so they can no longer sign in.
 */
export const offboardSchema = z.object({
  updatedAt: z.string().min(10),
  lastWorkingDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the format YYYY-MM-DD').refine(isRealDate, 'Not a real date'),
  reason: z.enum(OFFBOARD_REASONS),
  note: optionalText(500),
  returnStatus: trimmed(20).min(1),
  returnCondition: optionalText(20),
  releaseSeats: z.boolean().default(true),
  /** Email a return confirmation for each collected device (manager in CC). */
  notify: z.boolean().default(true),
  /**
   * The devices and seats the administrator saw and confirmed. If the person was given anything else after
   * the form opened, the server refuses rather than marking an uncollected device as returned.
   */
  deviceIds: z.array(uuid).max(1000).optional(),
  seatIds: z.array(uuid).max(1000).optional(),
});
export type OffboardInput = z.infer<typeof offboardSchema>;

export const reactivateSchema = z.object({ updatedAt: z.string().min(10), note: optionalText(500) });
export type ReactivateInput = z.infer<typeof reactivateSchema>;

// ── Audit ────────────────────────────────────────────────────

export const auditQuerySchema = z.object({
  q: trimmed(120).optional(),
  action: trimmed(120).optional(),
  role: trimmed(30).optional(),
  outcome: trimmed(20).optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(50),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

// ── Software & licences (dbo.SoftwareLicenses / dbo.LicenseAssignments) ──

export const LICENSE_SORT_FIELDS = ['licenseTag', 'productName', 'publisher', 'licenseType', 'seatsPurchased', 'expiryDate', 'status'] as const;
export type LicenseSortField = (typeof LICENSE_SORT_FIELDS)[number];

/** Renewal windows the UI offers. `expired` / `none` are facts; the day windows are views over ExpiryDate. */
export const LICENSE_EXPIRY_FILTERS = ['expired', '30', '90', 'none'] as const;

export const licenseQuerySchema = z.object({
  q: trimmed(120).optional(),
  type: trimmed(160).optional(),
  category: trimmed(240).optional(),
  publisher: trimmed(320).optional(),
  status: trimmed(40).optional(),
  expiry: z.enum(LICENSE_EXPIRY_FILTERS).optional(),
  seats: z.enum(['tracked', 'assigned']).optional(),
  sort: z.enum(LICENSE_SORT_FIELDS).default('licenseTag'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});
export type LicenseQuery = z.infer<typeof licenseQuerySchema>;

export const seatQuerySchema = z.object({
  q: trimmed(120).optional(),
  state: z.enum(['active', 'released', 'all']).default('active'),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});
export type SeatQuery = z.infer<typeof seatQuerySchema>;

export const seatAssignSchema = z.object({ rowVersion, employeeId: uuid, note: optionalText(500) });
export type SeatAssignInput = z.infer<typeof seatAssignSchema>;

export const seatReleaseSchema = z.object({ rowVersion, note: optionalText(500) });
export type SeatReleaseInput = z.infer<typeof seatReleaseSchema>;

// ── Auth ─────────────────────────────────────────────────────

export const devSignInSchema = z.object({ email: z.string().trim().toLowerCase().email('Enter a valid work email').max(320) });

export interface SessionUser {
  displayName: string;
  email: string;
  role: Role;
  employeeId: string | null;
  employeeCode: string | null;
  team: string | null;
  designation: string | null;
  capabilities: Capability[];
}

export interface SessionInfo {
  user: SessionUser;
  csrfToken: string;
  authMethod: 'development' | 'entra';
  expiresAt: string;
  idleTimeoutMinutes: number;
}

export interface AuthConfig {
  mode: 'development' | 'entra';
  entraConfigured: boolean;
  /**
   * How Entra sign-in runs: `server` = API code flow with a client secret; `spa` = MSAL.js in the
   * browser (code + PKCE, no secret) and the API verifies the ID token. Tenant and client IDs are
   * public identifiers (they appear in every Microsoft sign-in URL), never secrets.
   */
  entra?: { flow: 'server' | 'spa'; tenantId: string; clientId: string; scopes: string[] };
}

export const entraTokenSignInSchema = z.object({ idToken: z.string().min(20).max(16_000) });

// ── Response shapes ──────────────────────────────────────────

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface Meta {
  statuses: string[];
  assignedStatus: string;
  retiredStatus: string;
  availableStatus: string;
  categories: { name: string; isActive: boolean; isLaptop: boolean; assetCount: number }[];
  laptopCategories: string[];
  teams: { name: string; employees: number }[];
  locations: string[];
  handoverConditions: string[];
  retirementMethods: string[];
  sanitizationStates: string[];
  portalRoles: string[];
  /** Sender of custody confirmation emails. */
  mailFrom: string;
}

export interface AssetListItem {
  id: string;
  assetTag: string;
  name: string;
  assetType: string;
  isLaptop: boolean;
  model: string | null;
  vendor: string | null;
  serialNumber: string | null;
  status: string;
  location: string | null;
  assignee: { id: string; displayName: string; employeeCode: string; team: string | null } | null;
  assignedDate: string | null;
  updatedAt: string;
}

export interface AssignmentRecord {
  id: string;
  employee: { id: string; displayName: string; employeeCode: string; email: string; team: string | null } | null;
  assignedAt: string;
  assignedByName: string | null;
  returnedAt: string | null;
  returnStatus: string | null;
  returnNote: string | null;
  reason: string | null;
  ticketReference: string | null;
  handoverCondition: string | null;
  returnCondition: string | null;
  note: string | null;
}

export interface AuditRecord {
  id: string;
  occurredAt: string;
  actorName: string;
  actorEmail: string | null;
  actorRole: string;
  action: string;
  target: string;
  details: string | null;
  outcome: string | null;
  category: string | null;
  severity: string | null;
  before: unknown;
  after: unknown;
}

export interface AssetDetail extends AssetListItem {
  operatingSystem: string | null;
  cpu: string | null;
  ram: string | null;
  storage: string | null;
  purchaseDate: string | null;
  warrantyExpiry: string | null;
  notes: string | null;
  sourceSheet: string | null;
  sourceStatus: string | null;
  confidentiality: number | null;
  integrity: number | null;
  availability: number | null;
  totalCiaValue: number | null;
  recordKind: string;
  createdAt: string;
  rowVersion: string;
  assignments: AssignmentRecord[];
  retirement: { retiredAt: string; method: string; reason: string; sanitization: string | null; actorName: string } | null;
  audit: AuditRecord[];
}

export interface EmployeeListItem {
  id: string;
  employeeCode: string;
  displayName: string;
  email: string;
  team: string | null;
  designation: string | null;
  role: string | null;
  isActive: boolean;
  deviceCount: number;
  laptopCount: number;
  /** Other employee IDs this person has been known by (intern, overseas, contractor, previous). */
  otherIds: string[];
}

export interface EmployeeAlias {
  id: string;
  code: string;
  kind: EmployeeAliasKind;
  note: string | null;
  createdAt: string;
  createdBy: string;
}

export interface EmployeeDetail extends EmployeeListItem {
  managerName: string | null;
  managerEmail: string | null;
  dateOfJoining: string | null;
  /** Concurrency token for edits, offboarding and reactivation. */
  updatedAt: string;
  devices: AssetListItem[];
  /** Open named licence seats (empty for callers without licences.read). */
  openSeats: { seatId: string; licenseId: string; licenseTag: string; productName: string; assignedAt: string }[];
  /** The latest offboarding, when the person is inactive and was offboarded in the Hub. */
  offboarding: { at: string; by: string | null; lastWorkingDay: string | null; reason: string | null; note: string | null; devicesReturned: number; seatsReleased: number } | null;
  history: (AssignmentRecord & { asset: { id: string; assetTag: string; name: string; assetType: string } })[];
  aliases: EmployeeAlias[];
}

export interface CountRow {
  key: string;
  count: number;
}

export interface DashboardOverview {
  generatedAt: string;
  totals: { assets: number; assigned: number; employees: number; employeesWithDevices: number; categories: number };
  byStatus: CountRow[];
  byCategory: { category: string; isLaptop: boolean; total: number; byStatus: CountRow[] }[];
  recentActivity: AuditRecord[];
  dataQuality: DataQualitySignal[];
}

export interface DataQualitySignal {
  key: string;
  label: string;
  count: number;
  severity: 'info' | 'low' | 'medium';
  detail: string;
  link?: string;
}

export interface LaptopIntelligence {
  generatedAt: string;
  laptopCategories: string[];
  total: number;
  byStatus: CountRow[];
  teamAllocation: { rank: number; team: string; teamRecorded: boolean; laptops: number; employees: number; employeesWithLaptop: number; share: number }[];
  topModels: CountRow[];
  multiLaptopHolders: number;
  teamNameVariants: { normalized: string; variants: string[] }[];
}

export interface MyDevices {
  employee: { displayName: string; employeeCode: string; email: string; team: string | null; designation: string | null; managerName: string | null };
  devices: (AssetListItem & {
    operatingSystem: string | null;
    cpu: string | null;
    ram: string | null;
    storage: string | null;
    warrantyExpiry: string | null;
    assignedAt: string | null;
    handoverCondition: string | null;
  })[];
  history: { assetTag: string; name: string; assetType: string; isLaptop: boolean; model: string | null; assignedAt: string; returnedAt: string | null; returnStatus: string | null }[];
}

export interface LicenseListItem {
  id: string;
  licenseTag: string;
  productName: string;
  publisher: string | null;
  version: string | null;
  category: string | null;
  licenseType: string | null;
  status: string;
  /** Seats bought (null when the source does not count seats, e.g. volume-licence keys). */
  seatsPurchased: number | null;
  /** Seats in use as reported by the source system (e.g. the Microsoft 365 admin centre). */
  seatsInUseReported: number | null;
  /** Named seats recorded in the Hub (open dbo.LicenseAssignments rows). */
  seatsAssigned: number;
  renewalDate: string | null;
  expiryDate: string | null;
  updatedAt: string;
}

export interface LicenseSeat {
  id: string;
  employee: { id: string; displayName: string; employeeCode: string; email: string; team: string | null; isActive: boolean };
  assignedAt: string;
  assignedByName: string;
  note: string | null;
  releasedAt: string | null;
  releasedByName: string | null;
  releaseNote: string | null;
}

export interface LicenseDetail extends LicenseListItem {
  licenseReference: string | null;
  vendor: string | null;
  annualCost: number | null;
  costCurrency: string | null;
  confidentiality: number | null;
  integrity: number | null;
  availability: number | null;
  totalCiaValue: number | null;
  notes: string | null;
  createdAt: string;
  rowVersion: string;
  seats: LicenseSeat[];
  audit: AuditRecord[];
}

export interface SeatAssignmentRow extends LicenseSeat {
  license: { id: string; licenseTag: string; productName: string; publisher: string | null };
}

export interface LicenseFacets {
  statuses: string[];
  activeStatus: string | null;
  types: CountRow[];
  categories: CountRow[];
  publishers: CountRow[];
}

export interface LicenseInsights {
  generatedAt: string;
  total: number;
  byStatus: CountRow[];
  byType: CountRow[];
  byPublisher: CountRow[];
  /**
   * tracked/purchased: records that count seats (SeatsPurchased not null).
   * reportedRecords/purchasedReported/inUseReported: the subset that also reports usage, so
   * utilisation = inUseReported / purchasedReported compares like with like.
   */
  seats: { tracked: number; purchased: number; reportedRecords: number; purchasedReported: number; inUseReported: number; assignedInHub: number };
  /** Expiry figures cover in-force licences only (the discovered active status), when one exists. */
  expiryScope: string | null;
  expiry: { dated: number; expired: number; within30: number; within90: number; undated: number };
  renewals: (Pick<LicenseListItem, 'id' | 'licenseTag' | 'productName' | 'publisher' | 'expiryDate' | 'seatsPurchased' | 'status'> & { daysLeft: number })[];
  utilization: (Pick<LicenseListItem, 'id' | 'licenseTag' | 'productName' | 'publisher' | 'seatsPurchased' | 'seatsInUseReported' | 'seatsAssigned'> & { utilization: number })[];
  overAllocated: number;
  holders: number;
}

export interface SearchResults {
  assets: Pick<AssetListItem, 'id' | 'assetTag' | 'name' | 'assetType' | 'isLaptop' | 'status' | 'serialNumber' | 'assignee'>[];
  employees: Pick<EmployeeListItem, 'id' | 'employeeCode' | 'displayName' | 'email' | 'team' | 'deviceCount'>[];
  /** Present only for roles with licenses.read. */
  licenses?: Pick<LicenseListItem, 'id' | 'licenseTag' | 'productName' | 'publisher' | 'licenseType' | 'expiryDate'>[];
}

export type ImportRowKind = 'new' | 'update' | 'unchanged' | 'duplicate' | 'conflict' | 'invalid';

export interface ImportRowPlan {
  rowNumber: number;
  kind: ImportRowKind;
  assetTag: string | null;
  messages: string[];
  changes?: { field: string; from: unknown; to: unknown }[];
  values?: Record<string, unknown>;
  /** Asset version at analysis (update rows): commit refuses the row if it changed since. */
  rowVersion?: string;
}

export interface ImportPlan {
  batchId: string;
  fileName: string;
  sheetName: string;
  totals: Record<ImportRowKind, number> & { total: number };
  rows: ImportRowPlan[];
  status: string;
  createdAt: string;
  createdByName: string;
  committedAt: string | null;
}

export interface ApiErrorBody {
  error: { code: string; message: string; fields?: Record<string, string>; correlationId?: string };
}

/** Filter token meaning "assignee/employee has no team recorded" (dbo.Employees.Team IS NULL). */
export const NO_TEAM = '__none__';
export const NO_TEAM_LABEL = 'No team recorded';

// ── Monthly asset movements (reports) ────────────────────────

const yearMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');

export const movementQuerySchema = z.object({
  /** Inclusive month range; defaults to the last 12 months ending this month. */
  from: yearMonth.optional(),
  to: yearMonth.optional(),
  laptop: z.enum(['1']).optional(),
  type: trimmed(120).optional(),
});
export type MovementQuery = z.infer<typeof movementQuerySchema>;

export const movementDetailQuerySchema = z.object({
  month: yearMonth,
  laptop: z.enum(['1']).optional(),
  type: trimmed(120).optional(),
  kind: z.enum(['assigned', 'returned', 'added', 'retired', 'reinstated']).optional(),
});
export type MovementDetailQuery = z.infer<typeof movementDetailQuerySchema>;

/** Counts for one calendar month (UTC). `returnedTo` splits returns by the status the asset went back to. */
export interface MovementMonth {
  month: string;
  assigned: number;
  returned: number;
  returnedTo: CountRow[];
  added: number;
  retired: number;
  reinstated: number;
}

export interface MovementSummary {
  from: string;
  to: string;
  scope: { laptopsOnly: boolean; type: string | null };
  months: MovementMonth[];
  totals: { assigned: number; returned: number; added: number; retired: number; reinstated: number };
  generatedAt: string;
}

export type MovementKind = 'assigned' | 'returned' | 'added' | 'retired' | 'reinstated';

export interface MovementEvent {
  kind: MovementKind;
  at: string;
  assetId: string;
  assetTag: string;
  assetType: string;
  assetName: string;
  /** Employee who received (assigned) or handed back (returned) the asset. */
  employee: { id: string; name: string; employeeCode: string | null; team: string | null } | null;
  /** Who performed it, as recorded: assigned-by, returned-by, retired-by. */
  actor: string | null;
  /** Status the asset went back to on return, retirement method, or reason. */
  detail: string | null;
}

export interface MovementDetail {
  month: string;
  events: MovementEvent[];
  byActor: { kind: MovementKind; actor: string; count: number }[];
  byTeam: { team: string; assigned: number; returned: number }[];
}

// ── Warranty ─────────────────────────────────────────────────

export const WARRANTY_STATES = ['expired', 'within30', 'within90', 'active', 'unknown'] as const;
export type WarrantyState = (typeof WARRANTY_STATES)[number];

export const warrantyQuerySchema = z.object({
  q: trimmed(120).optional(),
  state: z.enum(WARRANTY_STATES).optional(),
  vendor: trimmed(160).optional(),
  type: trimmed(120).optional(),
  laptop: z.enum(['1']).optional(),
  includeRetired: z.enum(['1']).optional(),
  sort: z.enum(['daysLeft', 'assetTag', 'vendor', 'assetType']).default('daysLeft'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});
export type WarrantyQuery = z.infer<typeof warrantyQuerySchema>;

export interface WarrantyItem {
  id: string;
  assetTag: string;
  name: string;
  assetType: string;
  vendor: string | null;
  model: string | null;
  serialNumber: string | null;
  status: string;
  assignee: string | null;
  purchaseDate: string | null;
  warrantyExpiry: string | null;
  /** Whole days from today (UTC) to the expiry date; negative once expired; null when no date is recorded. */
  daysLeft: number | null;
  state: WarrantyState;
  rowVersion: string;
}

export interface WarrantySummary {
  /** Hardware in scope (retired assets excluded). */
  total: number;
  states: Record<WarrantyState, number>;
  byVendor: { vendor: string; total: number; recorded: number; active: number; expired: number }[];
  nextExpiries: WarrantyItem[];
  generatedAt: string;
}

/** Narrow edit used by the warranty register: only the two dates, with optimistic concurrency. */
export const warrantyUpdateSchema = z
  .object({ rowVersion, purchaseDate: isoDate, warrantyExpiry: isoDate })
  .refine((v) => !v.purchaseDate || !v.warrantyExpiry || v.warrantyExpiry >= v.purchaseDate, { message: 'Warranty cannot end before purchase', path: ['warrantyExpiry'] });
export type WarrantyUpdateInput = z.infer<typeof warrantyUpdateSchema>;

// ── Settings: IT administrators and recovery points ──────────

export interface ItAdministrator {
  id: string;
  employeeCode: string;
  displayName: string;
  email: string;
  team: string | null;
  designation: string | null;
  role: string;
  isSuperAdmin: boolean;
  devicesHeld: number;
  lastActiveAt: string | null;
}

export interface RecoveryPoint {
  id: string;
  label: string;
  createdAt: string;
  createdBy: string | null;
  assetCount: number | null;
  employeeCount: number | null;
  licenseCount: number | null;
  sizeKb: number;
  isBaseline: boolean;
  /** Only snapshots taken by this version can be restored; older (V2) snapshots are kept read-only. */
  restorable: boolean;
  format: 'v3' | 'legacy';
}

export interface RecoveryOverview {
  passwordSet: boolean;
  passwordUpdatedAt: string | null;
  points: RecoveryPoint[];
  /** Tables a V3 recovery point captures and restores. */
  scope: string[];
}

const recoveryPassword = z.string().min(1, 'Enter the recovery password').max(200);

export const recoveryPasswordSchema = z.object({
  currentPassword: z.string().max(200).optional(),
  newPassword: z
    .string()
    .min(12, 'Use at least 12 characters')
    .max(200)
    .refine((v) => /[A-Za-z]/.test(v) && /\d/.test(v), 'Use letters and at least one number'),
});
export const recoveryCreateSchema = z.object({ label: trimmed(120).min(3, 'Give the recovery point a name'), password: recoveryPassword });
export const recoveryRestoreSchema = z.object({ password: recoveryPassword, confirm: z.literal('RESTORE', { message: 'Type RESTORE to confirm' }) });
export const recoveryDeleteSchema = z.object({ password: recoveryPassword });

// ── Email notifications ──────────────────────────────────────

/** Outcome of a custody email: sent (Microsoft 365), saved (local outbox file), skipped, or failed. */
export interface CustodyNotification {
  status: 'sent' | 'saved' | 'skipped' | 'failed';
  to: string | null;
  cc: string | null;
  detail: string;
}

export interface EmailSettings {
  /** off = nothing sent; outbox = written to files for review; sendgrid = SendGrid API; graph = Microsoft 365. */
  mode: 'off' | 'outbox' | 'sendgrid' | 'graph';
  from: string;
  replyTo: string;
  /** Managers present in the register with an email, out of active employees. */
  managersRecorded: number;
  activeEmployees: number;
  recent: { at: string; action: string; target: string; details: string | null; outcome: string | null }[];
}

// ── IT Assistant (data-quality digest the chat assistant answers from) ──

export interface AssistantAssetRef {
  id: string;
  assetTag: string;
  name: string;
  assetType: string;
  status: string;
  serialNumber: string | null;
  assignee: string | null;
}

export interface AssistantPersonRef {
  id: string;
  employeeCode: string;
  displayName: string;
  email: string | null;
  team: string | null;
  isActive: boolean;
}

export interface AssistantDigest {
  generatedAt: string;
  /** UTC date every "days left" figure is counted from. */
  today: string;
  /** Groups of hardware records whose tag or serial is the same once case, spaces and - _ / are ignored. */
  duplicateAssetTags: { key: string; assets: AssistantAssetRef[] }[];
  duplicateSerials: { key: string; assets: AssistantAssetRef[] }[];
  /** People recorded more than once: same email, same name (active only), or the same employee ID. */
  duplicatePeople: { reason: 'email' | 'name' | 'employee-id'; key: string; people: AssistantPersonRef[] }[];
  /** Everyone holding two or more hardware records. */
  multiAssetHolders: (AssistantPersonRef & { devices: number; laptops: number; assets: string[] })[];
  /** People known by more than one employee ID (hub.EmployeeAliases). */
  multiIdPeople: (AssistantPersonRef & { otherIds: string[] })[];
  /** Former employees still holding hardware. */
  formerWithAssets: (AssistantPersonRef & { assets: string[] })[];
  /** Former employees still holding an open licence seat. */
  formerWithSeats: (AssistantPersonRef & { licences: string[] })[];
  /** Every licence with an expiry date up to a year ahead, including already-expired ones. */
  licences: { id: string; licenseTag: string; productName: string; publisher: string | null; status: string; inForce: boolean; expiryDate: string; daysLeft: number; seatsAssigned: number }[];
  /** Non-retired hardware with a warranty end date up to a year ahead, including already-expired ones. */
  warranty: (AssistantAssetRef & { warrantyExpiry: string; daysLeft: number })[];
  noSerial: AssistantAssetRef[];
  totals: { assets: number; activeEmployees: number; licences: number; warrantyNotRecorded: number };
}
